import { useEffect, useRef, useState } from 'react';
import { UserExam } from '../../types/User';
import { fetchVignetteCompletion } from '../../services/vignettes';
import { areAllValuesDefined } from '../../utils/miscUtils';
import { fetchFlashcardCompletion } from '../../services/flashcards';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../slices/exams/examsSlice';
import moment from 'moment';
import { fetchFirstStudySession } from '../../services/studySession';
import { lecturesSelectors } from '../../slices/lectures/lecturesSlice';
import { hapticsImpactLight } from '../../utils/haptics';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'react-feather';
import { isNbmeExam } from '../../utils/examUtils';
import { isSuperAdmin } from '../../utils/isSuperAdmin';
import { selectVideos } from '../../slices/videos/videosSlice';
import Widget from '..';
import { WIDGET_KEYS } from '../../types/Widgets';

interface ProgressProps {
  isDemo?: boolean;
  showDelete?: boolean;
}

const Progress: React.FC<ProgressProps> = ({
  isDemo = false,
  showDelete = false,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const lectures = useSelector(lecturesSelectors.selectAll);
  const progressBodyRef = useRef<HTMLDivElement>(null);
  const videos = useSelector(selectVideos);

  const [vignetteSeenPercentage, setVignetteSeenPercentage] = useState(
    isDemo ? 60 : 0
  );
  const [vignetteCorrectPercentage, setVignetteCorrectPercentage] = useState(
    isDemo ? 60 : 0
  );
  const [flashcardCompletionPercentage, setFlashcardCompletionPercentage] =
    useState(isDemo ? 50 : 0);
  const [timeRemainingProgress, setTimeRemainingProgress] = useState(0);

  const todayMarkerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (primaryExam && !isDemo) {
      calculateContentCompletion(primaryExam);
      calculateStudyProgress(primaryExam);
    }
  }, [primaryExam, isDemo]);

  useEffect(() => {
    const checkMarkerPosition = () => {
      if (todayMarkerRef.current && progressBodyRef.current) {
        const markerRect = todayMarkerRef.current.getBoundingClientRect();
        const bodyRect = progressBodyRef.current.getBoundingClientRect();
        const distanceFromLeft = markerRect.left - bodyRect.left;

        if (distanceFromLeft < 122) {
          todayMarkerRef.current.style.opacity = '0';
        } else {
          todayMarkerRef.current.style.opacity = '1';
        }
      }
    };

    checkMarkerPosition();
    window.addEventListener('resize', checkMarkerPosition);
    return () => window.removeEventListener('resize', checkMarkerPosition);
  }, [timeRemainingProgress]);

  const calculateContentCompletion = async (primaryExam: UserExam) => {
    if (currentUser && primaryExam) {
      if (primaryExam.isUsingQbank) {
        fetchVignetteCompletion(currentUser.id, primaryExam.id).then(
          (response) => {
            if (response) {
              setVignetteSeenPercentage(response.percentSeen);
              setVignetteCorrectPercentage(response.percentCorrect);
            }
          }
        );
      }
      if (primaryExam.isUsingFlashcards) {
        fetchFlashcardCompletion(currentUser.id, primaryExam.id).then(
          (response) => {
            if (response) {
              setFlashcardCompletionPercentage(response.percentCompleted);
            }
          }
        );
      }
    }
  };

  const calculateStudyProgress = async (primaryExam: UserExam) => {
    try {
      if (!primaryExam.examDate) {
        setTimeRemainingProgress(0);
        return;
      }

      const response = await fetchFirstStudySession(primaryExam.id);
      const endDate = moment.utc(primaryExam.examDate);
      const startDate = response?.date
        ? moment.utc(response.date)
        : moment.utc();
      const currentDate = moment.utc();
      const tDays = endDate
        .startOf('day')
        .diff(startDate.startOf('day'), 'days');
      const dRemaining = endDate
        .startOf('day')
        .diff(currentDate.startOf('day'), 'days');
      const progress = 100 - (dRemaining / tDays) * 100;

      if (progress < 0) {
        setTimeRemainingProgress(0);
      } else if (progress > 100) {
        setTimeRemainingProgress(100);
      } else {
        setTimeRemainingProgress(progress);
      }
    } catch (error) {
      console.error('Error calculating study progress:', error);
      setTimeRemainingProgress(0);
    }
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.PROGRESS}
      name={'progress'}
      title="Progress"
      subtitle={primaryExam?.name + ' completion of each content type'}
      showDelete={showDelete}
    >
      <div className="progress">
        <div
          className="progress_body"
          ref={progressBodyRef}
          style={
            lectures.length === 0 && primaryExam?.type === 'CUSTOM'
              ? {
                  padding: '0',
                }
              : {}
          }
        >
          {isSuperAdmin(currentUser?.id) && videos.length > 0 && (
            <div className="progress-bar">
              <div className="progress-bar_label">Videos</div>
              <div className="progress-bar_container">
                <div className="pb">
                  <div
                    className="pb_fill"
                    style={{
                      width: `${100}%`,
                      backgroundColor: 'var(--ct-videos)',
                    }}
                  />
                </div>
                <div className="pb-label">{100}%</div>
              </div>
            </div>
          )}
          {primaryExam?.isUsingQbank && (
            <>
              {areAllValuesDefined(
                vignetteSeenPercentage,
                vignetteCorrectPercentage
              ) && (
                <div className="progress-bar">
                  <div className="progress-bar_label">QBank</div>
                  <div className="progress-bar_container">
                    <div className="pb">
                      <div
                        className="pb_fill"
                        style={{
                          width: `${isNbmeExam(primaryExam) ? vignetteCorrectPercentage : vignetteSeenPercentage}%`,
                          backgroundColor: 'var(--ct-qbank)',
                        }}
                      />
                    </div>
                    <div className="pb-label">
                      {isNbmeExam(primaryExam)
                        ? vignetteCorrectPercentage
                        : vignetteSeenPercentage}
                      %
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {primaryExam?.isUsingFlashcards && (
            <>
              {areAllValuesDefined(flashcardCompletionPercentage) && (
                <div className="progress-bar">
                  <div className="progress-bar_label">Flashcards</div>
                  <div className="progress-bar_container">
                    <div className="pb">
                      <div
                        className="pb_fill"
                        style={{
                          width: `${flashcardCompletionPercentage}%`,
                          backgroundColor: 'var(--ct-flashcards)',
                        }}
                      />
                    </div>
                    <div className="pb-label">
                      {flashcardCompletionPercentage}%
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {lectures.length === 0 && primaryExam?.type === 'CUSTOM' && (
            <div
              className="button--add-lecture"
              role="button"
              tabIndex={0}
              onClick={() => {
                navigate('/modify-exam');
                hapticsImpactLight();
              }}
            >
              <Plus />
              Add Lectures to View Progress
            </div>
          )}
        </div>
      </div>
    </Widget>
  );
};

export default Progress;
